<template>
  <div class="register-small">
    <form class="register-small__form" @submit.prevent="submit">
      <!-- EMAIL -->
      <label class="register-small__label">
        Work email
        <input
          v-model="email"
          type="email"
          class="register-small__input"
          required
          @blur="emailBlurCheck"
          @input="emailInputCheck"
        />
      </label>

      <span v-if="errors.email.length" class="register-small__error">
        <div v-for="err in errors.email" :key="err">{{ err }}</div>
      </span>

      <!-- ORGANIZATION -->
      <label class="register-small__label">
        Organization
        <input v-model="organization" type="text" class="register-small__input" required />
      </label>

      <span v-if="errors.organization.length" class="register-small__error">
        <div v-for="err in errors.organization" :key="err">{{ err }}</div>
      </span>

      <!-- PERSONAL EMAIL CHECKBOX -->
      <label class="register-small__checkbox">
        <input type="checkbox" v-model="allowPersonalEmail" />
        I'm registering as an individual
      </label>

      <!-- PERSONAL EMAIL EXPLANATION -->
      <div v-if="allowPersonalEmail" class="register-small__personal">
        <p class="register-small__legend">
          We designed this platform for teams and organizations.
          <br /><br />
          If you're an individual researcher or small business, we’re still happy to support you —
          we just ask for a brief description of your intended work so we can ensure you get the
          right experience.
        </p>

        <label class="register-small__label">
          Tell us about your intended work
          <textarea
            v-model="personalEmailExplanation"
            class="register-small__textarea"
            aria-required="true"
          ></textarea>
        </label>

        <span v-if="errors.personalEmailExplanation.length" class="register-small__error">
          <div v-for="err in errors.personalEmailExplanation" :key="err">{{ err }}</div>
        </span>
      </div>

      <!-- DATASET TYPE -->
      <label class="register-small__label">
        Type of dataset
        <select v-model="datasetType" class="register-small__input">
          <option disabled value="">Select dataset type</option>
          <option>Customer / client feedback</option>
          <option>Complaints</option>
          <option>Social media posts</option>
          <option>Employee surveys</option>
          <option>Support tickets / live chat</option>
          <option>Other text (rows in an spreadsheet)</option>
        </select>
      </label>

      <span v-if="errors.datasetType.length" class="register-small__error">
        <div v-for="err in errors.datasetType" :key="err">{{ err }}</div>
      </span>

      <!-- ROLE -->
      <label class="register-small__label">
        What best describes your role?
        <select v-model="role" class="register-small__input" required>
          <option disabled value="">Select your role</option>
          <option>Researcher / academic</option>
          <option>Analyst / data professional</option>
          <option>Consultant</option>
          <option>Manager / director</option>
          <option>Student</option>
          <option>Other</option>
        </select>
      </label>

      <span v-if="errors.role.length" class="register-small__error">
        <div v-for="err in errors.role" :key="err">{{ err }}</div>
      </span>

      <!-- STUDENT NOTICE -->
      <div
        v-if="role === 'Student'"
        class="register-small__student-tip"
        :class="[
          'register-small__student-tip',
          studentAcknowledge ? 'register-small__student-tip--ok' : '',
        ]"
      >
        <p class="register-small__legend">
          Hello student! Our tool is optimized for regularly‑updating text data commonly found in
          organizations and businesses, such as spreadsheets with thousands of comments or
          complaints.
        </p>
        <p class="register-small__legend">
          Students are often disappointed to discover we are not a GPT chatbot, and are not
          optimized for transcripts, scanning other websites, literature reviews, or document
          summaries.
        </p>

        <label class="register-small__checkbox register-small__checkbox--student">
          <input type="checkbox" v-model="studentAcknowledge" />
          I understand
        </label>

        <span v-if="errors.studentAcknowledge.length" class="register-small__error">
          <div v-for="err in errors.studentAcknowledge" :key="err">{{ err }}</div>
        </span>
      </div>

      <!-- REFERRAL SOURCE -->
      <label class="register-small__label">
        How did you hear about ResearchAI?
        <select v-model="referral" class="register-small__input" required>
          <option disabled value="">Select an option</option>
          <option>Article series</option>
          <option>Conference</option>
          <option>Colleague / referral</option>
          <option>LinkedIn</option>
          <option>Web search</option>
          <option>GovMetric / partner</option>
          <option>Other</option>
        </select>
      </label>

      <span v-if="errors.referral.length" class="register-small__error">
        <div v-for="err in errors.referral" :key="err">{{ err }}</div>
      </span>

      <!-- PASSWORD -->
      <label v-if="canShowPasswordSection" class="register-small__label">
        Password
        <input
          v-model="password"
          type="password"
          class="register-small__input"
          required
          @input="passwordInputCheck"
          @blur="passwordBlurCheck"
        />
      </label>

      <span v-if="canShowPasswordSection && errors.password.length" class="register-small__error">
        <div v-for="err in errors.password" :key="err">{{ err }}</div>
      </span>

      <!-- PASSWORD CONFIRMATION -->
      <label v-if="canShowPasswordSection" class="register-small__label">
        Confirm Password
        <input
          v-model="passwordConf"
          type="password"
          class="register-small__input"
          required
          @blur="passwordConfBlurCheck"
        />
      </label>

      <span
        v-if="canShowPasswordSection && errors.passwordConf.length"
        class="register-small__error"
      >
        <div v-for="err in errors.passwordConf" :key="err">{{ err }}</div>
      </span>

      <!-- SUBMIT -->
      <button class="register-small__submit" :disabled="!canSubmit || isLoading">Sign up</button>

      <span v-if="isLoading" class="register-small__spinner spinner"></span>
    </form>
  </div>
</template>

<script>
import Validator from '@/pigeonline/lib/password/registerFormValidatior';

export default {
  name: 'RegisterSmall',

  data() {
    return {
      email: '',
      organization: '',
      datasetType: '',
      allowPersonalEmail: false,
      personalEmailExplanation: '',
      isLoading: false,
      password: '',
      passwordConf: '',
      role: '',
      referral: '',
      studentAcknowledge: false,
      consumerDomains: ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com'],
      validator: null,

      errors: {
        email: [],
        organization: [],
        datasetType: [],
        personalEmailExplanation: [],
        password: [],
        passwordConf: [],
        role: [],
        referral: [],
        studentAcknowledge: [],
      },
    };
  },

  computed: {
    isConsumerEmail() {
      if (!this.email.includes('@')) return false;
      const domain = this.email.split('@')[1]?.toLowerCase() || '';
      return this.consumerDomains.includes(domain);
    },
    canSubmit() {
      const hasValidEmail = this.email.includes('@') && !this.isConsumerEmail;
      const hasOrganization = this.organization.trim().length > 0;
      const hasDatasetType = !!this.datasetType;
      const hasRole = !!this.role;
      const studentOK = this.role !== 'Student' || this.studentAcknowledge;
      const hasReferral = !!this.referral;

      const passwordValid =
        this.password.trim().length >= 10 &&
        this.passwordConf.trim().length > 0 &&
        this.password === this.passwordConf &&
        this.errors.password.length === 0 &&
        this.errors.passwordConf.length === 0;

      // Case 1: business email path
      if (hasValidEmail && hasOrganization && hasDatasetType && passwordValid) {
        return true;
      }

      // Case 2: personal email path
      const personalEmailValid =
        this.allowPersonalEmail &&
        this.email.includes('@') &&
        this.isConsumerEmail &&
        this.personalEmailExplanation.trim().length > 0 &&
        this.errors.personalEmailExplanation.length === 0;

      if (personalEmailValid && hasDatasetType && passwordValid) {
        return true;
      }

      if (
        hasValidEmail &&
        hasOrganization &&
        hasDatasetType &&
        hasRole &&
        hasReferral &&
        studentOK &&
        passwordValid
      ) {
        return true;
      }

      if (
        personalEmailValid &&
        hasDatasetType &&
        hasRole &&
        studentOK &&
        hasReferral &&
        passwordValid
      ) {
        return true;
      }

      return false;
    },
    canShowPasswordSection() {
      const hasValidEmail = this.email.includes('@') && !this.isConsumerEmail;
      const hasOrganization = this.organization.trim().length > 0;
      const hasDatasetType = !!this.datasetType;
      const hasRole = !!this.role;
      const studentOK = this.role !== 'Student' || this.studentAcknowledge;
      const hasReferral = !!this.referral;

      // Personal email path
      const personalEmailValid =
        this.allowPersonalEmail &&
        this.email.includes('@') &&
        this.isConsumerEmail &&
        this.personalEmailExplanation.trim().length > 0 &&
        this.errors.personalEmailExplanation.length === 0;

      // Business email path
      if (hasValidEmail && hasOrganization && hasDatasetType && hasRole && hasReferral && studentOK)
        return true;
      // Personal email path
      if (personalEmailValid && hasDatasetType && hasRole && hasReferral && studentOK) return true;

      return false;
    },
  },

  mounted() {
    this.validator = new Validator(this.$data);
  },

  methods: {
    /* -----------------------------
       EMAIL VALIDATION
    ------------------------------*/
    emailBlurCheck() {
      const errs = this.validateEmail();

      if (this.allowPersonalEmail) {
        this.errors.email = errs.filter((e) => !e.includes('personal email'));
      } else {
        this.errors.email = errs;
      }
    },

    emailInputCheck() {
      const errs = this.validateEmail();

      if (this.allowPersonalEmail) {
        this.errors.email = errs.filter((e) => !e.includes('personal email'));
      } else {
        this.errors.email = errs;
      }
    },

    validateEmail() {
      const errs = [];
      if (!this.email.includes('@')) errs.push('Please enter a valid email.');
      if (this.isConsumerEmail)
        errs.push('This looks like a personal email. Please register as an individual.');

      return errs;
    },

    /* -----------------------------
       FORM VALIDATION
    ------------------------------*/
    validateForm() {
      this.errors.email = this.validateEmail();

      if (this.allowPersonalEmail) {
        this.errors.email = this.errors.email.filter((e) => !e.includes('personal email'));
      }

      this.errors.organization = [];
      if (!this.organization.trim()) {
        this.errors.organization.push('Organization is required.');
      }

      this.errors.datasetType = [];
      if (!this.datasetType) {
        this.errors.datasetType.push('Please select a dataset type.');
      }

      this.errors.personalEmailExplanation = [];
      if (this.allowPersonalEmail && !this.personalEmailExplanation.trim()) {
        this.errors.personalEmailExplanation.push('Please describe your intended work.');
      }

      this.errors.role = [];
      if (!this.role) {
        this.errors.role.push('Please select your role.');
      }

      this.errors.studentAcknowledge = [];
      if (this.role === 'Student' && !this.studentAcknowledge) {
        this.errors.studentAcknowledge.push('Please confirm that you understand.');
      }

      this.errors.referral = [];
      if (!this.referral) {
        this.errors.referral.push('Please tell us how you heard about ResearchAI.');
      }

      this.errors.password = this.validator.validate('password', 'submit');
      this.errors.passwordConf = this.validator.validate('passwordConf', 'submit');

      return Object.values(this.errors).flat().length === 0;
    },

    /* -----------------------------
       SUBMIT
    ------------------------------*/
    async submit() {
      if (!this.validateForm()) return;

      this.isLoading = true;

      try {
        const user = await this.$pigeonline.register(
          this.email,
          this.password,
          this.hasInviteId,
          this.personalEmailExplanation,
          this.datasetType,
          this.organization,
          this.role,
          this.referral,
        );

        this.isLoading = false;

        this.$emit('registered', user);
      } catch (e) {
        this.isLoading = false;

        const data = e.response?.data || {};

        Object.keys(this.errors).forEach((key) => {
          if (key in data) {
            this.errors[key] = data[key];
          }
        });
        this.message = 'There was an error performing registration. Please try again.';
      }
    },

    passwordInputCheck() {
      this.errors.password = this.validator.validate('password', 'input');
    },

    passwordBlurCheck() {
      this.errors.password = this.validator.validate('password', 'blur');
    },

    passwordConfBlurCheck() {
      this.errors.passwordConf = this.validator.validate('passwordConf', 'blur');
    },
  },
};
</script>

<style scoped>
.register-small {
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
}

.register-small__form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.register-small__label {
  display: flex;
  flex-direction: column;
  font-weight: 600;
  font-size: 0.9rem;
}

.register-small__input,
.register-small__textarea,
.register-small__select {
  margin-top: 0.25rem;
  padding: 0.55rem 0.9rem;
  border-radius: 12px;
  border: 1px solid #d1d5db;
  font-size: 0.9rem;
}

.register-small__textarea {
  min-height: 80px;
}

.register-small__checkbox {
  margin-top: -2rem !important;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 2rem;
}

.register-small__legend {
  font-size: 0.85rem;
  color: #6b7280;
  display: flex;
}

.register-small__error {
  color: #b91c1c;
  font-size: 1rem;
}

.register-small__submit {
  background: #1f7bbf;
  color: white;
  padding: 0.75rem;
  border-radius: 12px;
  border: none;
  font-size: 1rem;
  cursor: pointer;
}

.register-small__submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.register-small__student-tip {
  display: flex;
  flex-direction: column;
  padding: 0.75rem 1rem;
  background: #fef2f2; /* pale red background */
  border-left: 3px solid #dc2626; /* red-600 border */
  border-radius: 6px;
  color: #7f1d1d; /* red-800 text */
}

.register-small__checkbox--student {
  display: flex;
  margin-top: 0.15rem !important;
}

.register-small__student-tip--ok {
  background: #ecfdf5; /* green-50 */
  border-left-color: #059669; /* green-600 */
  color: #065f46; /* green-800 */
}
</style>
